import React, { useState } from 'react';
import { Upload, Download, Users, Home, Info } from 'lucide-react';
import api from '../services/api';
import { useCondominio } from '../contexts/CondominioContext';
import LoadingSpinner from '../components/LoadingSpinner';

const initialResultState = {
  processed: 0,
  created: 0,
  updated: 0,
  errors: []
};

const ConfiguracoesImportacao = () => {
  const { getCondominioId, condominioSelecionado } = useCondominio();
  const [moradoresFile, setMoradoresFile] = useState(null);
  const [unidadesFile, setUnidadesFile] = useState(null);
  const [isImportingMoradores, setIsImportingMoradores] = useState(false);
  const [isImportingUnidades, setIsImportingUnidades] = useState(false);
  const [moradoresResult, setMoradoresResult] = useState(initialResultState);
  const [unidadesResult, setUnidadesResult] = useState(initialResultState);
  const [errorMoradores, setErrorMoradores] = useState('');
  const [errorUnidades, setErrorUnidades] = useState('');

  const condominioId = getCondominioId();

  const downloadTemplate = async (endpoint, filenameSetter) => {
    try {
      const response = await api.get(endpoint.startsWith('/') ? endpoint : `/${endpoint}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filenameSetter);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao baixar template:', error);
      alert('Não foi possível baixar o template. Tente novamente.');
    }
  };

  const formatSummary = (result) => {
    if (!result) return '';
    const parts = [];
    if (result.created) parts.push(`${result.created} criado(s)`);
    if (result.updated) parts.push(`${result.updated} atualizado(s)`);
    if (parts.length === 0) return 'Nenhuma alteração realizada.';
    return parts.join(' · ');
  };

  const handleImportMoradores = async () => {
    setErrorMoradores('');
    setMoradoresResult(initialResultState);

    if (!moradoresFile) {
      setErrorMoradores('Selecione um arquivo CSV para importar moradores.');
      return;
    }

    if (!condominioId) {
      setErrorMoradores('Selecione um condomínio antes de importar.');
      return;
    }

    const formData = new FormData();
    formData.append('arquivo', moradoresFile);
    formData.append('condominio_id', condominioId);

    try {
      setIsImportingMoradores(true);
      const response = await api.post('/importacao/moradores', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setMoradoresResult(response.data.data || initialResultState);
      if (!response.data.success) {
        setErrorMoradores(response.data.message || 'Importação concluída com avisos.');
      }
      setMoradoresFile(null);
    } catch (error) {
      console.error('Erro ao importar moradores:', error);
      setErrorMoradores(error.response?.data?.message || 'Erro ao importar moradores.');
    } finally {
      setIsImportingMoradores(false);
    }
  };

  const handleImportUnidades = async () => {
    setErrorUnidades('');
    setUnidadesResult(initialResultState);

    if (!unidadesFile) {
      setErrorUnidades('Selecione um arquivo CSV para importar unidades.');
      return;
    }

    if (!condominioId) {
      setErrorUnidades('Selecione um condomínio antes de importar.');
      return;
    }

    const formData = new FormData();
    formData.append('arquivo', unidadesFile);
    formData.append('condominio_id', condominioId);

    try {
      setIsImportingUnidades(true);
      const response = await api.post('/importacao/unidades', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setUnidadesResult(response.data.data || initialResultState);
      if (!response.data.success) {
        setErrorUnidades(response.data.message || 'Importação concluída com avisos.');
      }
      setUnidadesFile(null);
    } catch (error) {
      console.error('Erro ao importar unidades:', error);
      setErrorUnidades(error.response?.data?.message || 'Erro ao importar unidades.');
    } finally {
      setIsImportingUnidades(false);
    }
  };

  if (!condominioId) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded">
          Selecione um condomínio para utilizar as rotinas de importação.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      <header>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Upload className="h-6 w-6 text-blue-600" />
          Importação de Dados
        </h1>
        <p className="text-gray-600 mt-1">
          Automatize o cadastro de moradores e unidades a partir de arquivos CSV padronizados.
        </p>
        {condominioSelecionado && (
          <p className="text-sm text-gray-500 mt-2">
            Condomínio selecionado: <strong>{condominioSelecionado.condominio_nome}</strong>
          </p>
        )}
      </header>

      <section className="bg-white rounded-lg shadow divide-y divide-gray-100">
        <div className="p-6 flex gap-4">
          <div className="flex-shrink-0">
            <Users className="h-10 w-10 text-blue-600" />
          </div>
          <div className="space-y-4 flex-1">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Importar Moradores</h2>
              <p className="text-sm text-gray-600 mt-1">
                Utilize este fluxo para cadastrar em massa os moradores de um condomínio.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-md p-4 flex gap-3 text-sm text-blue-900">
              <Info className="h-5 w-5 flex-shrink-0 text-blue-500" />
              <div>
                <p className="font-medium">Formato aceito</p>
                <p>Arquivo CSV com as colunas: nome, cpf, bloco, apartamento, telefone, email, tipo, ativo.</p>
                <p className="mt-2">
                  O sistema identifica moradores existentes pelo CPF. Caso já existam, eles serão atualizados.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() =>
                  downloadTemplate('/importacao/template/moradores', 'template-importacao-moradores.csv')
                }
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-blue-200 text-blue-600 rounded-md hover:bg-blue-50 transition-colors"
              >
                <Download className="h-4 w-4" />
                Baixar template
              </button>

              <label className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md cursor-pointer hover:bg-gray-200 transition-colors">
                <Upload className="h-4 w-4" />
                {moradoresFile ? moradoresFile.name : 'Selecionar arquivo CSV'}
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(event) => {
                    setMoradoresFile(event.target.files?.[0] || null);
                    setErrorMoradores('');
                  }}
                />
              </label>

              <button
                type="button"
                onClick={handleImportMoradores}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={isImportingMoradores}
              >
                {isImportingMoradores ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Importando...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Importar moradores
                  </>
                )}
              </button>
            </div>

            {errorMoradores && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                {errorMoradores}
              </div>
            )}

            {(moradoresResult.created > 0 ||
              moradoresResult.updated > 0 ||
              moradoresResult.errors.length > 0) && (
              <div className="bg-white border border-gray-200 rounded-md p-4">
                <p className="text-sm text-gray-600">
                  Processados: <strong>{moradoresResult.processed}</strong> · {formatSummary(moradoresResult)}
                </p>
                {moradoresResult.errors.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-semibold text-red-600">
                      Linhas com erro ({moradoresResult.errors.length})
                    </p>
                    <ul className="mt-2 space-y-1 text-sm text-gray-600 max-h-40 overflow-y-auto">
                      {moradoresResult.errors.map((item, index) => (
                        <li key={`${item.line}-${index}`}>
                          Linha {item.line}: {item.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="bg-white rounded-lg shadow divide-y divide-gray-100">
        <div className="p-6 flex gap-4">
          <div className="flex-shrink-0">
            <Home className="h-10 w-10 text-emerald-600" />
          </div>
          <div className="space-y-4 flex-1">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Importar Unidades</h2>
              <p className="text-sm text-gray-600 mt-1">
                Crie ou atualize apartamentos (unidades) em lote, incluindo o valor mensal do condomínio.
              </p>
            </div>

            <div className="bg-emerald-50 border border-emerald-100 rounded-md p-4 flex gap-3 text-sm text-emerald-900">
              <Info className="h-5 w-5 flex-shrink-0 text-emerald-500" />
              <div>
                <p className="font-medium">Formato aceito</p>
                <p>
                  Arquivo CSV com as colunas: bloco, apartamento, andar (opcional), valor_condominio, area_m2, quartos,
                  banheiros, vagas.
                </p>
                <p className="mt-2">
                  Unidades existentes serão atualizadas, novas serão criadas automaticamente.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() =>
                  downloadTemplate('/importacao/template/unidades', 'template-importacao-unidades.csv')
                }
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-emerald-200 text-emerald-600 rounded-md hover:bg-emerald-50 transition-colors"
              >
                <Download className="h-4 w-4" />
                Baixar template
              </button>

              <label className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md cursor-pointer hover:bg-gray-200 transition-colors">
                <Upload className="h-4 w-4" />
                {unidadesFile ? unidadesFile.name : 'Selecionar arquivo CSV'}
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(event) => {
                    setUnidadesFile(event.target.files?.[0] || null);
                    setErrorUnidades('');
                  }}
                />
              </label>

              <button
                type="button"
                onClick={handleImportUnidades}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={isImportingUnidades}
              >
                {isImportingUnidades ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Importando...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Importar unidades
                  </>
                )}
              </button>
            </div>

            {errorUnidades && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                {errorUnidades}
              </div>
            )}

            {(unidadesResult.created > 0 ||
              unidadesResult.updated > 0 ||
              unidadesResult.errors.length > 0) && (
              <div className="bg-white border border-gray-200 rounded-md p-4">
                <p className="text-sm text-gray-600">
                  Processados: <strong>{unidadesResult.processed}</strong> · {formatSummary(unidadesResult)}
                </p>
                {unidadesResult.errors.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-semibold text-red-600">
                      Linhas com erro ({unidadesResult.errors.length})
                    </p>
                    <ul className="mt-2 space-y-1 text-sm text-gray-600 max-h-40 overflow-y-auto">
                      {unidadesResult.errors.map((item, index) => (
                        <li key={`${item.line}-${index}`}>
                          Linha {item.line}: {item.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ConfiguracoesImportacao;

